<template>
  <div class="featured-player-container">
    <div class="fp-header">
      <div>{{ $t(data.category) }}</div>
    </div>
    <div class="fp-body">
      <div class="fp-photos is-centered">
        <figure
          class="image is-48x48 is-inline-flex has-margin-12"
          v-for="(featuredPlayer, index) in selectedFeaturedPlayers"
          :key="`img${index}`"
        >
          <img class="is-rounded has-green-border" :src="featuredPlayer.photo" />
        </figure>
      </div>
      <div class="fp-players">
        {{ names }}
      </div>
      <div class="fp-total-cont" @click="setOpenMedia(selectedFeaturedPlayers, top)">
        <div class="fp-total" :class="getStadisticId == 0 ? 'font-medium' : ''">
          {{ top }}<img v-if="this.isNumeric(top)" src="https://golstatsimages.blob.core.windows.net/todoelfut/playdes.png" />
        </div>
      </div>
    </div>
    <div class="fp-footer"></div>
  </div>
</template>

<script>
import teamStatsPlayIndexes from '@/data/team/teamStatsPlayIndexes';
import { mapGetters, mapState, mapMutations } from 'vuex';
const INDEXES = teamStatsPlayIndexes();
export default {
  name: 'FeaturedPlayer',
  props: {
    data: {
      type: Object,
      required: true,
    },
    plays: {
      type: Array,
      required: true,
    },
    players: {
      type: Array,
      required: true,
    },
    negative: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapGetters('filterGeneral', {
      getStadisticId: 'getStadisticId',
      getGamesPlayedByTeam: 'getGamesPlayedByTeam',
      gamesPlayedByPlayer: 'getGamesPlayedByPlayer',
      getResults: 'getResults',
      getSections: 'getSections',
      getPlayingAs: 'getPlayingAs',
      getSelectedGamesIds: 'getSelectedGamesIds',
      seasonSelected: 'getSeasonId',
    }),
    ...mapState('teamsGeneral', ['teamPlaysByPlayer']),
    ...mapState('filterGeneral', ['tournamentsForMediaPlayer']),
    gamesPlayed() {
      return this.getGamesPlayedByTeam === 0 ? 1 : this.getGamesPlayedByTeam;
    },
    /**
     * Selecciona las jugadas de la categoría correspondiente.
     * @author José Rafael Gutierrez
     * @return Array
     */
    allPlayCategory() {
      return this.plays.filter(play => this.data.category_id.includes(play[INDEXES.categoryId]));
    },
    /**
     * Selecciona los jugadores destacados de la categoría si los hubiese.
     * @author José Rafael Gutierrez
     * @return Array
     */
    selectedFeaturedPlayers() {
      let players = [];
      this.players.forEach(player => {
        const gamesPlayed = this.gamesPlayedByPlayer(this.teamPlaysByPlayer.get(player.player_id));
        players.push({
          id: player.player_id,
          name: player.short_name,
          photo: player.photo,
          participation: +(
            this.allPlayCategory.filter(play => play[INDEXES.playerId] === player.player_id).length / (gamesPlayed || 1)
          ).toFixed(2),
        });
      });
      if (players.length === 0) return [];
      players = players.sort((a, b) => b.participation - a.participation);
      const TOTAL = players[0].participation;
      if (TOTAL === 0) return [];
      return players.filter(fp => fp.participation === TOTAL).slice(0, 3);
    },
    /**
     * Retorna un array del nombre o los nombres de los jugadores destacados de la categoría.
     * @author José Rafael Gutierrez
     * @return Array
     */
    namePlayers() {
      return this.selectedFeaturedPlayers.map(fp => fp.name.replace(',', '').trim());
    },
    /**
     * Retorna un String del nombre o los nombres de los jugadores destacados de la categoría.
     * @author José Rafael Gutierrez
     * @return String
     */
    names() {
      return this.namePlayers.length ? this.namePlayers.join(' - ') : ' - ';
    },
    /**
     * Retorna el total de jugadas máxima de la categoría según jugador/es destacado/s.
     * @author José Rafael Gutierrez
     * @return Number
     */
    total() {
      return this.selectedFeaturedPlayers.length ? this.selectedFeaturedPlayers[0].participation : 0;
    },
    /**
     * Retorna el total de jugadas máxima de la categoría según jugador/es destacado/s con un formato específico.
     * @author José Rafael Gutierrez
     * @return String
     */
    top() {
      return this.total > 0 ? new Intl.NumberFormat('en-US', { useGrouping: true }).format(this.total) : ' - ';
    },
  },
  methods: {
    ...mapMutations('mediaModal', ['setModalInitialState', 'setIsModalOpen']),
    isNumeric(str) {
      if (typeof str != 'string') return false;
      return !isNaN(str) && !isNaN(parseFloat(str));
    },
    setOpenMedia(players, value) {
      if (this.isNumeric(value)) {
        const jsonConfig = {
          seasonId: this.seasonSelected,
          tournaments: this.tournamentsForMediaPlayer,
          isFeaturedPlayers: true,
          categoryIds: [...this.data.category_id],
          categorySingleName: this.data.singleName,
          mainSectionType: 1,
          playListType: 2,
          gameIds: this.getSelectedGamesIds,
          matchResult: this.getResults,
          playData: {},
          playerIds: [],
          teamId: 0,
          advancedFilter: {
            playListTitle: this.$t(this.data.category),
            mainCategoryId: {},
            playingAs: this.getPlayingAs ? [this.getPlayingAs] : [1, 2],
            timeSections: this.getSections,
            fieldSections: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
          },
        };
        players.forEach(player => {
          jsonConfig.playerIds.push(player.id);
        });
        this.setModalInitialState(jsonConfig);
        this.setIsModalOpen(true);
      }
    },
  },
};
</script>

<style scoped>
.font-medium {
  font-size: 54px !important;
}
.featured-player-container {
  display: inline-block;
  width: 268px;
  height: 268px;
  border-radius: 21px;
  box-shadow: 1px 1px 3px 0 rgba(0, 0, 0, 0.5);
  border: solid 1px #7ed321;
  background-color: #ffffff;
}
.featured-player-container .fp-header {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 52px;
  border-radius: 21px 21px 0 0;
  background-color: #7ed321;
  font-family: 'Avenir-Demi';
  font-size: 18px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.22;
  letter-spacing: normal;
  text-align: center;
  color: #ffffff;
  padding: 0 10px;
}

.featured-player-container .fp-photos {
  height: 72px;
  padding-left: 10px;
  padding-top: 10px;
}

.featured-player-container .fp-players {
  font-family: 'Avenir-Demi';
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.06;
  letter-spacing: normal;
  text-align: center;
  color: #808285;
  padding-left: 10px;
  padding-right: 10px;
  height: 36px;
}
.featured-player-container .fp-total-cont {
  width: 85%;
  border-radius: 50px;
  border: solid 2px rgba(126, 211, 33, 0.5);
  margin: 0 auto;
  height: 78px;
  display: inline-flex;
  justify-content: center;
  vertical-align: middle;
  align-items: center;
}
.featured-player-container .fp-total {
  font-family: 'Avenir-Demi';
  font-size: 70px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.06;
  display: inline-flex;
  justify-content: center;
  vertical-align: middle;
  align-items: center;
  letter-spacing: normal;
  text-align: center;
  color: #808285;
  margin: 0 auto;
}
.featured-player-container .fp-total-cont:hover {
  opacity: 0.8;
  cursor: pointer;
}
.featured-player-container .fp-total > img {
  width: 40px;
  height: 40px;
  margin-left: 10px;
}

.has-green-border {
  border: solid 2px #7ed321;
}
.has-margin-12 {
  margin: 6px 3px;
}
</style>
